import { reactive, UnwrapRef, ref, Ref, computed, watch } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import { formatSize } from '@/renderer/scripts/utils/tools/index';
import store from '@/renderer/store';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

type TableData = {
  list: Array<object>;
  selectedRowKeys: Array<string>;
  selectedList: Array<object>;
};
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};

type LoadMoreData = {
  list: Array<object>;
  keyword: string;
  page: number;
  pageSize: number;
  total: number;
};
type PullAllUserListParams = {
  keyword: string;
  withSpaceUsage?: boolean;
  pageSize?: number;
};
type Handler = {
  searchStatus: Ref<boolean>;
  keyword: Ref<string>;
  columns: Array<any>;
  loading: Ref<Boolean>;
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  sort: Sort;
  hasMore: Ref<boolean>;
  loadMoreData: LoadMoreData;
  methods: {
    formatList: (originList: any[]) => any[];
    pullUserList: () => Promise<void>;
    pullAllUserList: ({ keyword, withSpaceUsage, pageSize }: PullAllUserListParams) => Promise<SMH.UserBaseInfo[]>;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
    refresh: () => void;
    searchList: () => void;
    onPageSizeChange: (pageSize: number) => void;
    onCurrentChange: (current: number) => void;
  };
};
export default function pullListHandler(currentUser?: Ref<any>): Handler {
  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      disabled: ({ row }: { row: SMH.UserBaseInfo }) => {
        // 任何人不能操作自己
        if (row.id === currentUser?.value.userId) {
          return true;
        }
        // 普通管理员不能操作管理员
        if (currentUser?.value.role === 'admin') {
          return row.role !== 'user';
        }
        // 兜底普通用户无操作权限
        if (currentUser?.value.role === 'user') {
          return true;
        }
        return false;
      },
      width: 30,
    },
    {
      colKey: 'nickname',
      title: 'title-nickname',
      titleDesc: i18n_t('用户昵称'),
      width: 150,
      sorter: true,
    },
    {
      colKey: 'role',
      title: 'title-role',
      titleDesc: i18n_t('身份'),
      ellipsis: true,
      width: 120,
      sorter: true,
    },
    {
      colKey: 'space',
      title: i18n_t('个人空间'),
      width: 180,
    },
    store.getters.onlyPasswordLogin
      ? {
          colKey: 'accountName',
          title: i18n_t('用户账号'),
          ellipsis: true,
          width: 140,
        }
      : {
          colKey: 'phoneNumber',
          title: i18n_t('手机号'),
          ellipsis: true,
          width: 140,
        },
    {
      colKey: 'enabled',
      title: 'title-enabled',
      titleDesc: i18n_t('状态'),
      ellipsis: true,
      width: 160,
      sorter: true,
    },
  ];

  // 用户账号登录时需展示初始化状态
  if (store.getters.onlyPasswordLogin) {
    columns.splice(5, 0, {
      colKey: 'needChangePassword',
      title: 'title-needChangePassword',
      titleDesc: i18n_t('初始化状态'),
      ellipsis: true,
      width: 100,
      sorter: true,
    });
  }

  // 不允许手机号登录 & 非账密登录需移除手机号列
  if (!store.getters.allowPhoneLogin && !store.getters.onlyPasswordLogin) {
    columns.splice(4, 1);
  }
  const loading: Ref<boolean> = ref(false);
  const keyword: Ref<string> = ref('');
  const searchStatus: Ref<boolean> = ref(false);
  const tableData = reactive<TableData>({
    selectedRowKeys: [],
    selectedList: [],
    list: [],
  });
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
        value: 50,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
        value: 100,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
        value: 200,
      },
    ],
  });

  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('innerUser', {
    sortBy: 'role',
    descending: true,
  });

  const loadMoreData = reactive<LoadMoreData>({
    list: [],
    keyword: '',
    page: 1,
    pageSize: 20,
    total: 0,
  });

  // 加载更多按钮
  const hasMore = computed(() => loadMoreData.list.length < loadMoreData.total);

  // 企业剩余空间大小
  const remainingSpaceQuota = computed(() => {
    const sizeInfo = formatSize(store.getters.remainingSpaceQuota);
    return {
      size: sizeInfo.size || '0',
      unit: sizeInfo.unit || 'B',
    };
  });
  const methods = {
    formatList(originList: any[]) {
      const list = originList.map((item: any) => {
        let usedPercent = 0;
        let size = 0; // 配额
        let unit = 'B';
        let used = 0; // 已使用
        let usedUnit = 'B';
        const spaceTotal = item.capacity || store.getters.remainingSpaceQuota; // 没有配额时代表共享企业剩余空间
        const spaceUsed = item.size || '0';
        // 表格内展示小数点格式
        if (spaceTotal) {
          const sizeInfo = formatSize(spaceTotal);
          size = sizeInfo.size;
          unit = sizeInfo.unit;
        }
        if (spaceUsed) {
          const usedInfo = formatSize(`${spaceUsed}`);
          used = usedInfo.size;
          usedUnit = usedInfo.unit;
          usedPercent = (spaceUsed / spaceTotal) * 100;
        }
        used = Math.max(0, used);
        // eslint-disable-next-line no-nested-ternary
        usedPercent = usedPercent > 100 ? 100 : usedPercent < 0 ? 0 : usedPercent;
        if (!item.capacity) {
          // 没有个人空间额度时剩余可用为共享企业剩余空间
          item.spaceRemainingSize = remainingSpaceQuota.value.size;
          item.spaceRemainingUnit = remainingSpaceQuota.value.unit;
        } else {
          // 有个人空间额度时剩余可用为配额-已使用
          const spaceRemainingInfo = formatSize(item.capacity - spaceUsed);
          item.spaceRemainingSize = spaceRemainingInfo.size;
          item.spaceRemainingUnit = spaceRemainingInfo.unit;
        }
        // 兼容后台返回null
        item.email = item.email || '';
        item.comment = item.comment || '';
        return {
          ...item,
          size,
          unit,
          used,
          usedUnit,
          usedPercent,
        };
      });
      return list;
    },
    async pullUserList() {
      try {
        loading.value = true;
        const res = await smh.userAPI.getUserList({
          keyword: keyword.value,
          page: pageData.currPage,
          pageSize: pageData.pageSize,
          withSpaceUsage: true,
          showPhoneNumber: true,
          orderBy: innerSort.value.orderBy,
          orderByType: innerSort.value.orderByType,
        });
        if (keyword.value && pageData.total !== 0) {
          searchStatus.value = true;
        } else {
          searchStatus.value = false;
        }
        loading.value = false;
        pageData.total = res.data.totalNum;
        tableData.list = methods.formatList(res.data.contents);
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
      } catch (e) {
        loading.value = false;
        tips.error(e as MsgError);
      }
    },
    // 循环拉取所有用户,比如搜索时使用
    async pullAllUserList({ keyword = '', withSpaceUsage = false, pageSize = 50 }) {
      const loop = async (page: number, list: SMH.UserBaseInfo[]): Promise<SMH.UserBaseInfo[]> => {
        loading.value = true;
        try {
          loading.value = true;
          const res = await smh.userAPI.getUserList({
            keyword,
            page,
            pageSize,
            withSpaceUsage,
            showPhoneNumber: true,
            orderBy: innerSort.value.orderBy,
            orderByType: innerSort.value.orderByType,
            includeInactiveUser: 0, // 全量搜索也不需要未激活用户
          });
          loading.value = false;
          const total = res.data.totalNum;
          list.push(...methods.formatList(res.data.contents));
          // 需要继续查询
          if (list.length < total) {
            loop(page + 1, list);
          } else {
            return list;
          }
        } catch (e) {
          loading.value = false;
          return list;
        }
        list.forEach((item: any) => (item.name = item.nickname));
        return list;
      };
      const allUserList = await loop(1, []);
      return allUserList;
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullUserList();
    },
    refresh() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      tableData.list = [];
      methods.pullUserList();
    },
    searchList() {
      pageData.currPage = 1;
      methods.refresh();
    },
    onPageSizeChange(pageSize: number) {
      pageData.pageSize = pageSize;
      methods.pullUserList();
    },
    onCurrentChange(current: number) {
      pageData.currPage = current;
      methods.pullUserList();
    },
    // 更多加载
    async loadUserList() {
      try {
        loading.value = true;
        const res = await smh.userAPI.getUserList({
          keyword: loadMoreData.keyword,
          page: loadMoreData.page,
          pageSize: loadMoreData.pageSize,
        });
        loading.value = false;
        loadMoreData.total = res.data.totalNum;
        const list = res.data.contents.map(item => ({
          id: item.id,
          avatar: item.avatar,
          nickname: item.nickname,
          phoneNumber: item.phoneNumber,
          checked: false,
        }));
        loadMoreData.list = [...loadMoreData.list, ...list];
      } catch (e) {
        loading.value = false;
        tips.error(e as MsgError);
      }
    },
  };
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  return {
    searchStatus,
    keyword,
    columns,
    loading,
    tableData,
    pageData,
    sort: tableSort,
    hasMore,
    loadMoreData,
    methods,
  };
}
